import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MoreVertical,
  Eye,
  Edit2,
  Megaphone,
  Trash2,
  FireExtinguisher,
  Lock,
  Shield,
  AlertCircle,
} from "lucide-react";
import { useOfferPermissions } from "../../hooks/permissionHooks/useOfferPermissions";
import { toast } from "react-toastify";

const PermissionAwareActionDropdown = ({
  id,
  activeDropdown,
  setActiveDropdown,
  handleViewModal,
  deal,
  handleEditNavigation,
  handleBlastSMSCampaign,
  handleDeleteClick,
  isExpired,
  handleOpenPromoteModal,
  handleOpenDemoteModal,
}) => {
  const [dropdownPosition, setDropdownPosition] = useState("top");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const dropdownRef = useRef(null);

  const {
    canEditOffers,
    canDeleteOffers,
    canBlastDeals,
    getPermissionInfo,
    isMainVendor,
  } = useOfferPermissions();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown === id) {
      document.addEventListener("mousedown", handleClickOutside);
      const button = document.getElementById(`action-button-${id}`);
      if (button) {
        const rect = button.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 320;
        const newPosition = spaceBelow < dropdownHeight ? "top" : "bottom";
        setDropdownPosition(newPosition);

        setDropdownStyle({
          position: "fixed",
          right: window.innerWidth - rect.right,
          ...(newPosition === "top"
            ? { bottom: window.innerHeight - rect.top + 8 }
            : { top: rect.bottom + 8 }),
        });
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown, id, setActiveDropdown]);

  // Build actions array with permission awareness
  const actions = [];

  // View action - always available
  actions.push({
    icon: <Eye className="w-4 h-4" />,
    label: "View Details",
    color: "text-blue-600",
    onClick: () => handleViewModal(id, deal),
    available: true,
    description: "View offer details and information",
  });

  // Promotion actions - for non-expired, non-pending deals
  if (!isExpired(deal.expiryDate) && deal.status !== "Pending") {
    if (deal.promote_deal !== 1) {
      actions.push({
        icon: <Megaphone className="w-4 h-4" />,
        label: "Promote Deal",
        color: canBlastDeals ? "text-pink-600" : "text-gray-400",
        onClick: canBlastDeals ? () => handleOpenPromoteModal(deal.deal_slug) : null,
        available: canBlastDeals,
        restricted: !canBlastDeals,
        description: canBlastDeals 
          ? "Promote this deal to featured status" 
          : "Permission required to promote deals",
        permissionInfo: getPermissionInfo('blast'),
      });
    } else {
      actions.push({
        icon: <FireExtinguisher className="w-4 h-4" />,
        label: "Demote Deal",
        color: canBlastDeals ? "text-pink-600" : "text-gray-400",
        onClick: canBlastDeals ? () => handleOpenDemoteModal(deal.deal_slug) : null,
        available: canBlastDeals,
        restricted: !canBlastDeals,
        description: canBlastDeals 
          ? "Remove featured status from this deal" 
          : "Permission required to demote deals",
        permissionInfo: getPermissionInfo('blast'),
      });
    }

    // Blast Deal action
    actions.push({
      icon: <Megaphone className="w-4 h-4" />,
      label: "Blast Deal",
      color: canBlastDeals ? "text-green-600" : "text-gray-400",
      onClick: canBlastDeals ? () => handleBlastSMSCampaign(deal) : null,
      available: canBlastDeals,
      restricted: !canBlastDeals,
      description: canBlastDeals 
        ? "Send SMS campaign for this deal" 
        : "Permission required to blast deals",
      permissionInfo: getPermissionInfo('blast'),
    });
  }

  // Edit action (always available)
  actions.push({
    icon: <Edit2 className="w-4 h-4" />,
    label: "Edit Offer",
    color: canEditOffers ? "text-green-600" : "text-gray-400",
    onClick: canEditOffers ? () => handleEditNavigation(deal) : null,
    available: canEditOffers,
    restricted: !canEditOffers,
    description: canEditOffers 
      ? "Edit offer details and information" 
      : "Permission required to edit offers",
    permissionInfo: getPermissionInfo('edit'),
  });

  // Delete action
  actions.push({
    icon: <Trash2 className="w-4 h-4" />,
    label: "Delete Offer",
    color: canDeleteOffers ? "text-red-600" : "text-gray-400",
    onClick: canDeleteOffers ? () => handleDeleteClick(deal) : null,
    available: canDeleteOffers,
    restricted: !canDeleteOffers,
    description: canDeleteOffers 
      ? "Permanently delete this offer" 
      : "Permission required to delete offers",
    permissionInfo: getPermissionInfo('delete'),
  });

  const handleActionClick = (action, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    if (action.available && action.onClick) {
      action.onClick();
    } else if (action.restricted) {
      toast.error(
        action.permissionInfo?.errorMessage || 
        "You don't have permission for this action"
      );
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        id={`action-button-${id}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={(e) => {
          e.stopPropagation();
          setActiveDropdown(activeDropdown === id ? null : id);
        }}
        className="p-2 rounded-lg transition-colors duration-200 hover:bg-gray-100"
      >
        <MoreVertical className="w-5 h-5 text-gray-600" />
      </motion.button>

      <AnimatePresence>
        {activeDropdown === id && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            className="fixed w-72 rounded-xl bg-white shadow-xl border border-gray-100 py-2 z-[99999]"
            style={dropdownStyle}
          >
            {/* Header */}
            <div className="px-4 py-2 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Actions for {deal.title}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="py-1">
              {actions.map((action, index) => (
                <motion.div
                  key={`${action.label}-${index}`}
                  whileHover={action.available ? { x: 4 } : {}}
                  className="relative"
                >
                  <button
                    onClick={(e) => handleActionClick(action, e)}
                    disabled={action.restricted}
                    className={`w-full px-4 py-3 flex items-center transition-all duration-200
                      ${action.available 
                        ? `hover:bg-gray-50 ${action.color}` 
                        : 'cursor-not-allowed opacity-60'
                      }`}
                    title={action.description}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="flex items-center space-x-2">
                        {action.icon}
                        {action.restricted && (
                          <Lock className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <span className="text-sm font-medium block">
                          {action.label}
                        </span>
                        <span className="text-xs text-gray-500 block">
                          {action.description}
                        </span>
                      </div>
                    </div>
                    
                    {action.restricted && (
                      <div className="flex items-center">
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                      </div>
                    )}
                  </button>

                  {/* Permission tooltip for restricted actions */}
                  {action.restricted && action.permissionInfo && (
                    <div className="px-4 py-2 bg-amber-50 border-l-4 border-amber-400">
                      <div className="flex items-center gap-2 mb-1">
                        <Shield className="w-3 h-3 text-amber-600" />
                        <span className="text-xs font-medium text-amber-800">
                          Permission Required
                        </span>
                      </div>
                      <p className="text-xs text-amber-700">
                        {action.permissionInfo.description}
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Footer with user info */}
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Role: {isMainVendor ? "Main Vendor" : "Team Member"}
                </span>
                <span className="text-xs text-gray-500">
                  {actions.filter(a => a.available).length} of {actions.length} actions available
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PermissionAwareActionDropdown; 